import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BackdropLoader, PasswordInput, toast } from '@livable/saas-react-components';
import { track, events } from 'src/configs/amplitude';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Typography, TextField, Button, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useFormik } from 'formik';
import { Link as RouterLink } from 'react-router-dom';
import InputMask from 'react-input-mask';
import { mfaLoginInitialValues, loginInitialValues, mfaLoginSchema, loginSchema } from 'src/utils/formValidation/Login';
import { useStore } from 'src/stores';
import { beforeSubmitDataValidation } from 'src/core/services/beforeSubmitDataValidation';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import SplitPageContainer from 'src/components/shared/SplitPageContainer';
import LoginLeftContent from 'src/components/shared/LoginLeftContent';
import onlineReportSvg from 'src/img/online-report.svg';
import { api } from 'src/api';
import useStyles from './styles';
export const LoginPage = observer(() => {
  const store = useStore();
  const classes = useStyles();
  const navigate = useNavigate();
  const page = 'Login';
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMfaLogin = _useState2[0],
    setIsMfaLogin = _useState2[1];
  const _useState3 = useState({
      email: '',
      session: ''
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    loginData = _useState4[0],
    setLoginData = _useState4[1];
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const isLoggedIn = api.Account.isLoggedIn();
  const urlParams = new URLSearchParams(window.location.search);
  const autologinUsername = urlParams.get('username');
  const autologinTempPassword = urlParams.get('temppassword');
  const loginFormik = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginSchema,
    onSubmit: async values => {
      const data = {
        email: values.email,
        password: values.password
      };
      const resp = await store.account.loginAccountV2Async.run(beforeSubmitDataValidation(data));
      if (resp && resp.isSuccessful && !resp.response.requireMfa && !store.account.userProfile.isUserAdmin && !store.account.userProfile.isUserSuperAdmin) {
        await store.company.getCurrentUserCompanyFromTokenAsync.run();
      }
      if (resp && resp.isSuccessful) {
        if (resp.response.requireNewPassword) {
          localStorage.setItem('INVITED_USER_EMAIL', data.email);
          localStorage.setItem('INVITED_USER_SESSION', resp.response.session);
          navigate('/set_password');
          return;
        }
        if (resp.response.session) {
          setIsMfaLogin(true);
          setLoginData({
            email: data.email,
            session: resp.response.session
          });
        } else {
          const isCompany = localStorage.getItem(USER_SETTINGS_KEYS.company);
          try {
            const hsResp = await store.hubspot.getHubspotTokenAsync.run();
            if (hsResp && hsResp.isSuccessful) {
              window.hsConversationsSettings = {
                identificationEmail: hsResp.response.email,
                identificationToken: hsResp.response.token
              };
              window.HubSpotConversations.resetAndReloadWidget();
            }
          } catch (error) {}
          if (!isCompany) {
            navigate('/admin');
            toast('success', {
              body: ['Please select company to continue work', store.admin.companyId]
            });
          } else {
            await store.account.getProfileAsync.run();
            if (isCompany && isCompany !== null && isCompany !== 'null') {
              try {
                await store.payment.getPaymentMethodsAsync.run();
              } catch (error) {}
              await store.portfolios.getExistingPortfoliosAsync.run();
            }
            track(events.loggedIn(), {
              page
            });
            navigate('/active_dashboard');
          }
        }
      } else {
        if (store.account.loginAccountV2Async.error.errorCode === 'NotConfirmedAccount') {
          localStorage.setItem(USER_SETTINGS_KEYS.confirmation, data.email);
          navigate('/profile_saved');
          return;
        }
      }
    }
  });
  const mfaLoginFormik = useFormik({
    initialValues: mfaLoginInitialValues,
    validationSchema: mfaLoginSchema,
    onSubmit: async values => {
      const data = _objectSpread({
        verificationCode: "".concat(values.verificationCode.replace(/[^0-9+]/g, ''))
      }, loginData);
      const resp = await store.account.mfaLoginAccountAsync.run(data);
      if (resp && resp.isSuccessful) {
        const isCompany = localStorage.getItem(USER_SETTINGS_KEYS.company);
        try {
          if (store.hubspot.getHubspotTokenAsync.inProgress) return;
          const hsResp = await store.hubspot.getHubspotTokenAsync.run();
          if (hsResp && hsResp.isSuccessful) {
            window.hsConversationsSettings = {
              identificationEmail: hsResp.response.email,
              identificationToken: hsResp.response.token
            };
            window.HubSpotConversations.resetAndReloadWidget();
          }
        } catch (error) {}
        if (!isCompany) {
          navigate('/admin');
          toast('success', {
            body: ['Please select company to continue work', store.admin.companyId]
          });
          return;
        }
        await store.account.getProfileAsync.run();
        await store.company.getCurrentUserCompanyFromTokenAsync.run();
        await store.portfolios.getExistingPortfoliosAsync.run();
        if (isCompany !== 'null') {
          await store.payment.getPaymentMethodsAsync.run();
        }
        track(events.loggedIn(), {
          page
        });
        navigate('/active_dashboard');
      }
    }
  });
  const inviteNewUser = async () => {
    const logoutResp = await store.account.logoutAccountAsync.run();
    if (!logoutResp || !logoutResp.isSuccessful) {
      store.auth.reset();
    }
    navigate("/set_password".concat(window.location.search));
  };
  useEffect(() => {
    if (isLoggedIn && !autologinUsername && !autologinTempPassword) {
      navigate('/');
      return;
    }
    if (isLoggedIn && autologinUsername && autologinTempPassword) {
      store.account.setIsInvite(true);
      return;
    }
    if (autologinUsername && autologinTempPassword) {
      navigate("/set_password".concat(window.location.search));
    }
    loginFormik.setFieldTouched('email', true);
    loginFormik.setFieldTouched('password', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, store.account.isInvite ? /*#__PURE__*/React.createElement(SplitPageContainer, {
    imageScr: onlineReportSvg,
    leftBlock: /*#__PURE__*/React.createElement(LoginLeftContent, null),
    largeTopMargin: true
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3",
    marginBottom: "40px"
  }, "You are trying to continue as a different user, this will log you out of the current session, do you agree to this?"), /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%'
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    onClick: inviteNewUser,
    'custom-attribute': '27-login-page'
  }, "Yes")), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    component: RouterLink,
    to: "/",
    'custom-attribute': '28-login-page'
  }, "No"))))) : /*#__PURE__*/React.createElement(SplitPageContainer, {
    imageScr: onlineReportSvg,
    leftBlock: /*#__PURE__*/React.createElement(LoginLeftContent, null),
    largeTopMargin: true
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3",
    marginBottom: "40px"
  }, t('log_in_flow.log_into_pm_portal')), isMfaLogin && /*#__PURE__*/React.createElement("div", {
    className: classes.loginPageForm
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: mfaLoginFormik.handleSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(InputMask, {
    mask: "99-99-99",
    value: mfaLoginFormik.values.verificationCode,
    onChange: mfaLoginFormik.handleChange,
    onBlur: e => {
      if (e.target.value) mfaLoginFormik.handleBlur(e);
    },
    disabled: store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress
  }, inputProps => /*#__PURE__*/React.createElement(TextField, _extends({}, inputProps, {
    id: "verificationCode",
    name: "verificationCode",
    label: t('log_in_flow.verification_code'),
    variant: "outlined",
    type: "tel",
    fullWidth: true,
    disabled: store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress,
    helperText: mfaLoginFormik.touched.verificationCode && t(mfaLoginFormik.errors.verificationCode),
    error: mfaLoginFormik.touched.verificationCode && Boolean(mfaLoginFormik.errors.verificationCode)
  })))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    disabled: !mfaLoginFormik.isValid || !Object.keys(mfaLoginFormik.touched).length || store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress,
    'custom-attribute': '33-login-page'
  }, t('buttons.sign_in')))))), !isMfaLogin && /*#__PURE__*/React.createElement("div", {
    className: classes.loginPageForm
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: loginFormik.handleSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 24
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    id: "email",
    name: "email",
    label: t('general.email_address'),
    variant: "outlined",
    type: "email",
    fullWidth: true,
    disabled: store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress,
    value: loginFormik.values.email,
    onBlur: e => {
      if (e.target.value) {
        loginFormik.handleBlur(e);
        track(events.filled('Email'), {
          page
        });
      }
    },
    onChange: loginFormik.handleChange,
    InputLabelProps: {
      shrink: true
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(PasswordInput, {
    label: t('general.password'),
    id: "password",
    variant: "outlined",
    name: "password",
    fullWidth: true,
    disabled: store.account.loginAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress,
    value: loginFormik.values.password,
    onBlur: e => {
      if (e.target.value) {
        loginFormik.handleBlur(e);
        track(events.filled('Password'), {
          page
        });
      }
    },
    onChange: loginFormik.handleChange,
    labelShrink: true
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.actionButtonWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    component: RouterLink,
    variant: "actionButton",
    to: "/reset_password",
    disabled: store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress,
    onClick: () => track(events.clicked('Forgot Password'), {
      page
    }),
    'custom-attribute': '34-login-page'
  }, t('general.forgot_password')))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "priority",
    fullWidth: true,
    disabled: !loginFormik.isValid || !Object.keys(loginFormik.touched).length || store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress,
    'custom-attribute': '35-login-page',
    onClick: () => track(events.clicked('Sign In'), {
      page
    })
  }, t('buttons.sign_in'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Button, {
    component: RouterLink,
    variant: "text",
    to: "/",
    fullWidth: true,
    disabled: store.account.getProfileAsync.inProgress || store.account.logoutAccountAsync.inProgress || store.account.loginAccountV2Async.inProgress || store.hubspot.getHubspotTokenAsync.inProgress || store.account.mfaLoginAccountAsync.inProgress || store.payment.getPaymentMethodsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.company.getCurrentUserCompanyFromTokenAsync.inProgress,
    'custom-attribute': '36-login-page',
    onClick: () => track(events.clicked('Go To Sign Up'), {
      page
    })
  }, t('buttons.dont_have_account'))))), /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: store.account.loginInviteAsync.inProgress
  }))));
});
LoginPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};